import { useState, useCallback, useMemo, useEffect, useRef } from "react";

export type PipeSize = '1/2"' | '3/4"' | '1"' | '1-1/4"' | '1-1/2"' | '2"' | '2-1/2"' | '3"' | '4"' | '6"' | '8"' | '10"' | '12"';
export type PipeMaterial = "Copper" | "Steel" | "PVC" | "CPVC" | "Stainless" | "Cast Iron";
export type FittingType = "elbow90" | "elbow45" | "tee" | "reducer" | "coupling" | "valve" | "cap";
export type SketchMode = "draw" | "fitting" | "select" | "delete" | "pan";
export type IsoAxis = "right" | "left" | "up";

export interface GridPoint {
  gx: number;
  gy: number;
}

export interface PipeSegment {
  id: string;
  start: GridPoint;
  end: GridPoint;
  size: PipeSize;
  material: PipeMaterial;
  lengthFt: number;
  axis: IsoAxis;
}

export interface Fitting {
  id: string;
  type: FittingType;
  position: GridPoint;
  size: PipeSize;
  material: PipeMaterial;
  rotation: number;
  /** Screen-space angle (radians) of incoming pipe direction (points away from junction) */
  inAngle?: number;
  /** Screen-space angle (radians) of outgoing pipe direction (points away from junction) */
  outAngle?: number;
  /** Screen-space angle (radians) of branch direction for tee fittings */
  branchAngle?: number;
  /** Whether this fitting was auto-inserted at a direction change */
  auto?: boolean;
}

export interface CutListItem {
  size: PipeSize;
  material: PipeMaterial;
  segments: { id: string; lengthFt: number }[];
  totalFt: number;
  fittings: { type: FittingType; count: number }[];
}

const GRID_SPACING = 40;
const SCALE_FT_PER_UNIT = 1;

export function gridToIso(gx: number, gy: number): { x: number; y: number } {
  const isoX = (gx - gy) * (GRID_SPACING * Math.cos(Math.PI / 6));
  const isoY = (gx + gy) * (GRID_SPACING * Math.sin(Math.PI / 6));
  return { x: isoX, y: isoY };
}

export function snapToGrid(screenX: number, screenY: number, offsetX: number, offsetY: number): GridPoint {
  const cosA = GRID_SPACING * Math.cos(Math.PI / 6);
  const sinA = GRID_SPACING * Math.sin(Math.PI / 6);
  const sx = screenX - offsetX;
  const sy = screenY - offsetY;
  const gx = (sx / cosA + sy / sinA) / 2;
  const gy = (sy / sinA - sx / cosA) / 2;
  return { gx: Math.round(gx), gy: Math.round(gy) };
}

/** Get screen-space direction angle (radians) from point `from` toward point `to` */
export function getDirectionAngle(from: GridPoint, to: GridPoint): number {
  const dgx = to.gx - from.gx;
  const dgy = to.gy - from.gy;
  const cos30 = Math.cos(Math.PI / 6);
  const sin30 = Math.sin(Math.PI / 6);
  const screenDx = (dgx - dgy) * cos30;
  const screenDy = (dgx + dgy) * sin30;
  return Math.atan2(screenDy, screenDx);
}

function calcSegmentLength(start: GridPoint, end: GridPoint): number {
  const dx = Math.abs(end.gx - start.gx);
  const dy = Math.abs(end.gy - start.gy);
  if (dx === dy && dx !== 0) {
    // Vertical axis: movement is along the isometric vertical (equal gx & gy change)
    return dx * SCALE_FT_PER_UNIT;
  }
  return Math.max(dx, dy) * SCALE_FT_PER_UNIT;
}

export function determineAxis(start: GridPoint, end: GridPoint): IsoAxis {
  const dx = end.gx - start.gx;
  const dy = end.gy - start.gy;
  // Vertical: both gx and gy change by the same amount (and same sign = up, opposite would be odd)
  if (dx !== 0 && dy !== 0 && Math.abs(dx) === Math.abs(dy)) return "up";
  if (dx !== 0 && dy === 0) return "right";
  if (dx === 0 && dy !== 0) return "left";
  return "right"; // fallback
}

function samePoint(a: GridPoint, b: GridPoint): boolean {
  return a.gx === b.gx && a.gy === b.gy;
}

/** Check if point P lies strictly between A and B on the same grid axis */
function pointOnSegment(p: GridPoint, segStart: GridPoint, segEnd: GridPoint): boolean {
  const sameGx = segStart.gx === segEnd.gx;
  const sameGy = segStart.gy === segEnd.gy;

  if (sameGy && p.gy === segStart.gy) {
    const minGx = Math.min(segStart.gx, segEnd.gx);
    const maxGx = Math.max(segStart.gx, segEnd.gx);
    return p.gx > minGx && p.gx < maxGx;
  }
  if (sameGx && p.gx === segStart.gx) {
    const minGy = Math.min(segStart.gy, segEnd.gy);
    const maxGy = Math.max(segStart.gy, segEnd.gy);
    return p.gy > minGy && p.gy < maxGy;
  }
  // Vertical axis: diagonal in grid space (equal dx/dy ratio)
  if (!sameGx && !sameGy) {
    const dxSeg = segEnd.gx - segStart.gx;
    const dySeg = segEnd.gy - segStart.gy;
    if (Math.abs(dxSeg) === Math.abs(dySeg)) {
      // Check p is on the same diagonal
      const dxP = p.gx - segStart.gx;
      const dyP = p.gy - segStart.gy;
      if (Math.abs(dxP) === Math.abs(dyP) && dxP !== 0 &&
          Math.sign(dxP) * Math.sign(dySeg) === Math.sign(dyP) * Math.sign(dxSeg)) {
        const t = dxP / dxSeg;
        return t > 0 && t < 1;
      }
    }
  }
  return false;
}

/** Get the stroke width for a given pipe size */
export function pipeStrokeWidth(size: PipeSize): number {
  const map: Record<PipeSize, number> = {
    '1/2"': 2.5, '3/4"': 3, '1"': 3.5, '1-1/4"': 4, '1-1/2"': 4.5,
    '2"': 5, '2-1/2"': 5.5, '3"': 6, '4"': 7, '6"': 8, '8"': 9, '10"': 10, '12"': 11,
  };
  return map[size] || 4;
}

let nextId = 1;
const genId = () => `s${nextId++}`;

const STORAGE_KEY = "pipe-sketch-data";
const SKETCHES_KEY = "pipe-sketch-list";
const ACTIVE_KEY = "pipe-sketch-active";

interface SketchData {
  segments: PipeSegment[];
  fittings: Fitting[];
}

export interface SavedSketch {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  data: SketchData;
}

function loadSketchList(): SavedSketch[] {
  try {
    const raw = localStorage.getItem(SKETCHES_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function saveSketchList(list: SavedSketch[]) {
  localStorage.setItem(SKETCHES_KEY, JSON.stringify(list));
}

function loadActiveId(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

function loadSaved(): SketchData {
  const activeId = loadActiveId();
  if (activeId) {
    const list = loadSketchList();
    const found = list.find(s => s.id === activeId);
    if (found) return found.data;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { segments: [], fittings: [] };
}

export function useSketchState() {
  const saved = useMemo(() => loadSaved(), []);
  const [segments, setSegments] = useState<PipeSegment[]>(saved.segments);
  const [fittings, setFittings] = useState<Fitting[]>(saved.fittings);
  const [undoStack, setUndoStack] = useState<SketchData[]>([]);
  const [redoStack, setRedoStack] = useState<SketchData[]>([]);
  const [mode, setMode] = useState<SketchMode>("draw");
  const [activeSize, setActiveSize] = useState<PipeSize>('1"');
  const [activeMaterial, setActiveMaterial] = useState<PipeMaterial>("Copper");
  const [activeFitting, setActiveFitting] = useState<FittingType>("elbow90");
  const [drawStart, setDrawStart] = useState<GridPoint | null>(null);
  const [sketchList, setSketchList] = useState<SavedSketch[]>(() => loadSketchList());
  const [activeSketchId, setActiveSketchId] = useState<string | null>(() => loadActiveId());

  // Refs for stable access in callbacks
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const fittingsRef = useRef(fittings);
  fittingsRef.current = fittings;

  // Auto-save
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ segments, fittings }));
    if (activeSketchId) {
      setSketchList(prev => {
        const updated = prev.map(s =>
          s.id === activeSketchId
            ? { ...s, data: { segments, fittings }, updatedAt: new Date().toISOString() }
            : s
        );
        saveSketchList(updated);
        return updated;
      });
    }
  }, [segments, fittings, activeSketchId]);

  const saveAsNewSketch = useCallback((name: string) => {
    const id = `sketch-${Date.now()}`;
    const now = new Date().toISOString();
    const newSketch: SavedSketch = {
      id, name, createdAt: now, updatedAt: now,
      data: { segments, fittings },
    };
    setSketchList(prev => {
      const updated = [...prev, newSketch];
      saveSketchList(updated);
      return updated;
    });
    setActiveSketchId(id);
    localStorage.setItem(ACTIVE_KEY, id);
    return id;
  }, [segments, fittings]);

  const loadSketch = useCallback((id: string) => {
    const sketch = sketchList.find(s => s.id === id);
    if (!sketch) return;
    setSegments(sketch.data.segments);
    setFittings(sketch.data.fittings);
    setUndoStack([]);
    setRedoStack([]);
    setDrawStart(null);
    setActiveSketchId(id);
    localStorage.setItem(ACTIVE_KEY, id);
  }, [sketchList]);

  const deleteSketch = useCallback((id: string) => {
    setSketchList(prev => {
      const updated = prev.filter(s => s.id !== id);
      saveSketchList(updated);
      return updated;
    });
    if (activeSketchId === id) {
      setActiveSketchId(null);
      localStorage.removeItem(ACTIVE_KEY);
    }
  }, [activeSketchId]);

  const renameSketch = useCallback((id: string, name: string) => {
    setSketchList(prev => {
      const updated = prev.map(s => s.id === id ? { ...s, name } : s);
      saveSketchList(updated);
      return updated;
    });
  }, []);

  const newBlankSketch = useCallback(() => {
    setSegments([]);
    setFittings([]);
    setUndoStack([]);
    setRedoStack([]);
    setDrawStart(null);
    setActiveSketchId(null);
    localStorage.removeItem(ACTIVE_KEY);
  }, []);

  const pushUndo = useCallback(() => {
    setUndoStack(prev => [...prev.slice(-30), { segments, fittings }]);
    setRedoStack([]);
  }, [segments, fittings]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack(r => [...r, { segments, fittings }]);
      setSegments(last.segments);
      setFittings(last.fittings);
      return prev.slice(0, -1);
    });
  }, [segments, fittings]);

  const redo = useCallback(() => {
    setRedoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setUndoStack(u => [...u, { segments, fittings }]);
      setSegments(last.segments);
      setFittings(last.fittings);
      return prev.slice(0, -1);
    });
  }, [segments, fittings]);

  /**
   * Add a pipe segment and auto-insert fittings at direction changes.
   * If the new segment starts where an existing segment ends (or vice versa)
   * and the direction changes, a 90° elbow is auto-inserted at the junction.
   */
  const addSegment = useCallback((start: GridPoint, end: GridPoint) => {
    if (samePoint(start, end)) return;
    pushUndo();

    const newAxis = determineAxis(start, end);
    const currentSegments = segmentsRef.current;
    const currentFittings = fittingsRef.current;

    // Find any existing segment that connects at the start point
    const connectedSeg = currentSegments.find(s =>
      samePoint(s.end, start) || samePoint(s.start, start)
    );

    let newFittings = [...currentFittings];
    let newSegments = [...currentSegments];
    let didSplitForTee = false;

    // --- Tee detection: does the start point lie on the middle of an existing segment? ---
    const existingFittingAtStart = currentFittings.find(f => samePoint(f.position, start));
    if (!existingFittingAtStart) {
      const hitSeg = currentSegments.find(s => pointOnSegment(start, s.start, s.end));
      if (hitSeg) {
        // Split the existing segment into two at the start point
        const seg1: PipeSegment = {
          id: genId(),
          start: hitSeg.start,
          end: { ...start },
          size: hitSeg.size,
          material: hitSeg.material,
          lengthFt: calcSegmentLength(hitSeg.start, start),
          axis: hitSeg.axis,
        };
        const seg2: PipeSegment = {
          id: genId(),
          start: { ...start },
          end: hitSeg.end,
          size: hitSeg.size,
          material: hitSeg.material,
          lengthFt: calcSegmentLength(start, hitSeg.end),
          axis: hitSeg.axis,
        };

        // Replace the original segment with the two halves
        newSegments = newSegments.filter(s => s.id !== hitSeg.id);
        newSegments.push(seg1, seg2);
        didSplitForTee = true;

        // Insert tee fitting — compute the three direction angles
        // The two directions along the split segment, plus the new branch
        const dirA = getDirectionAngle(start, hitSeg.start);
        const dirB = getDirectionAngle(start, hitSeg.end);
        const dirBranch = getDirectionAngle(start, end);

        const teeFitting: Fitting = {
          id: genId(),
          type: "tee",
          position: { ...start },
          size: hitSeg.size,
          material: hitSeg.material,
          rotation: 0,
          inAngle: dirA,
          outAngle: dirB,
          branchAngle: dirBranch,
          auto: true,
        };
        newFittings = [...newFittings, teeFitting];
      }
    }

    // --- Elbow detection at endpoint connections (only if we didn't already place a tee) ---
    if (!didSplitForTee && !existingFittingAtStart) {
      const connectedSeg = (didSplitForTee ? newSegments : currentSegments).find(s =>
        samePoint(s.end, start) || samePoint(s.start, start)
      );

      if (connectedSeg && connectedSeg.axis !== newAxis) {
        const otherEnd = samePoint(connectedSeg.end, start) ? connectedSeg.start : connectedSeg.end;
        const inAngle = getDirectionAngle(start, otherEnd);
        const outAngle = getDirectionAngle(start, end);

        const elbowFitting: Fitting = {
          id: genId(),
          type: "elbow90",
          position: { ...start },
          size: activeSize,
          material: activeMaterial,
          rotation: 0,
          inAngle,
          outAngle,
          auto: true,
        };
        newFittings = [...newFittings, elbowFitting];
      }
    }

    const seg: PipeSegment = {
      id: genId(),
      start,
      end,
      size: activeSize,
      material: activeMaterial,
      lengthFt: calcSegmentLength(start, end),
      axis: determineAxis(start, end),
    };

    setFittings(newFittings);
    setSegments(didSplitForTee ? [...newSegments, seg] : [...currentSegments, seg]);
  }, [activeSize, activeMaterial, pushUndo]);

  const addFitting = useCallback((position: GridPoint) => {
    pushUndo();
    const fit: Fitting = {
      id: genId(),
      type: activeFitting,
      position,
      size: activeSize,
      material: activeMaterial,
      rotation: 0,
    };
    setFittings(prev => [...prev, fit]);
  }, [activeFitting, activeSize, activeMaterial, pushUndo]);

  const deleteAtPoint = useCallback((point: GridPoint) => {
    pushUndo();
    setSegments(prev => prev.filter(s =>
      !samePoint(s.start, point) && !samePoint(s.end, point)
    ));
    setFittings(prev => prev.filter(f => !samePoint(f.position, point)));
  }, [pushUndo]);

  const clearAll = useCallback(() => {
    pushUndo();
    setSegments([]);
    setFittings([]);
  }, [pushUndo]);

  /**
   * Merge any pipe endpoints / fitting positions that are within `radius` grid units
   * of each other into a single representative point. Cleans up zero-length segments
   * and duplicate fittings sharing a position. Pure helper — returns updated arrays.
   */
  const mergeCoincident = useCallback((
    segs: PipeSegment[],
    fits: Fitting[],
    radius = 0.5,
  ): { segments: PipeSegment[]; fittings: Fitting[] } => {
    type Pt = { gx: number; gy: number };
    // Gather every distinct point referenced by segments/fittings
    const points: Pt[] = [];
    for (const s of segs) { points.push(s.start); points.push(s.end); }
    for (const f of fits) { points.push(f.position); }
    if (points.length === 0) return { segments: segs, fittings: fits };

    // Cluster points by union-find on proximity
    const parent = points.map((_, i) => i);
    const find = (i: number): number => parent[i] === i ? i : (parent[i] = find(parent[i]));
    const union = (a: number, b: number) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const d = Math.hypot(points[i].gx - points[j].gx, points[i].gy - points[j].gy);
        if (d <= radius) union(i, j);
      }
    }
    // Compute representative point per cluster (rounded average → snaps to grid)
    const clusters = new Map<number, { sumGx: number; sumGy: number; count: number }>();
    for (let i = 0; i < points.length; i++) {
      const r = find(i);
      const c = clusters.get(r) || { sumGx: 0, sumGy: 0, count: 0 };
      c.sumGx += points[i].gx; c.sumGy += points[i].gy; c.count++;
      clusters.set(r, c);
    }
    const repFor = (i: number): Pt => {
      const c = clusters.get(find(i))!;
      return { gx: Math.round(c.sumGx / c.count), gy: Math.round(c.sumGy / c.count) };
    };

    // Apply representatives back. Track index as we walk the same order we added them.
    let idx = 0;
    const newSegs: PipeSegment[] = [];
    for (const s of segs) {
      const start = repFor(idx++);
      const end = repFor(idx++);
      // Drop segments that collapsed to a point
      if (start.gx === end.gx && start.gy === end.gy) continue;
      newSegs.push({ ...s, start, end, lengthFt: calcSegmentLength(start, end), axis: determineAxis(start, end) });
    }
    const newFits: Fitting[] = [];
    const seenAtPos = new Map<string, Fitting>();
    for (const f of fits) {
      const position = repFor(idx++);
      const key = `${position.gx},${position.gy}`;
      const existing = seenAtPos.get(key);
      if (existing) {
        // Prefer manual (non-auto) fittings; otherwise keep the first one we saw.
        if (existing.auto && !f.auto) {
          const replacement = { ...f, position };
          seenAtPos.set(key, replacement);
          const i2 = newFits.findIndex(x => x.id === existing.id);
          if (i2 >= 0) newFits[i2] = replacement;
        }
        continue;
      }
      const merged = { ...f, position };
      seenAtPos.set(key, merged);
      newFits.push(merged);
    }
    return { segments: newSegs, fittings: newFits };
  }, []);

  /**
   * Move a single segment by a grid delta. Any fittings that sit on either
   * endpoint of the segment are dragged along (so connected joints stay glued).
   * After moving, nearly-coincident endpoints are merged into a single connection.
   */
  const moveSegment = useCallback((id: string, dgx: number, dgy: number) => {
    if (dgx === 0 && dgy === 0) return;
    pushUndo();
    const seg = segmentsRef.current.find(s => s.id === id);
    if (!seg) return;
    const oldStart = seg.start;
    const oldEnd = seg.end;
    const movedSegs = segmentsRef.current.map(s => s.id === id
      ? { ...s, start: { gx: s.start.gx + dgx, gy: s.start.gy + dgy }, end: { gx: s.end.gx + dgx, gy: s.end.gy + dgy } }
      : s
    );
    const movedFits = fittingsRef.current.map(f => {
      if (samePoint(f.position, oldStart) || samePoint(f.position, oldEnd)) {
        return { ...f, position: { gx: f.position.gx + dgx, gy: f.position.gy + dgy } };
      }
      return f;
    });
    const merged = mergeCoincident(movedSegs, movedFits);
    setSegments(merged.segments);
    setFittings(merged.fittings);
  }, [pushUndo, mergeCoincident]);

  /** Move a single fitting by a grid delta, then merge coincident endpoints. */
  const moveFitting = useCallback((id: string, dgx: number, dgy: number) => {
    if (dgx === 0 && dgy === 0) return;
    pushUndo();
    const movedFits = fittingsRef.current.map(f => f.id === id
      ? { ...f, position: { gx: f.position.gx + dgx, gy: f.position.gy + dgy } }
      : f
    );
    const merged = mergeCoincident(segmentsRef.current, movedFits);
    setSegments(merged.segments);
    setFittings(merged.fittings);
  }, [pushUndo, mergeCoincident]);

  // Computed cut list
  const cutList = useMemo<CutListItem[]>(() => {
    const map = new Map<string, CutListItem>();
    for (const seg of segments) {
      const key = `${seg.size}|${seg.material}`;
      if (!map.has(key)) {
        map.set(key, { size: seg.size, material: seg.material, segments: [], totalFt: 0, fittings: [] });
      }
      const item = map.get(key)!;
      item.segments.push({ id: seg.id, lengthFt: seg.lengthFt });
      item.totalFt += seg.lengthFt;
    }
    for (const fit of fittings) {
      const mat = fit.material || activeMaterial;
      const key = `${fit.size}|${mat}`;
      if (!map.has(key)) {
        map.set(key, { size: fit.size, material: mat, segments: [], totalFt: 0, fittings: [] });
      }
      const item = map.get(key)!;
      const existing = item.fittings.find(f => f.type === fit.type);
      if (existing) existing.count++;
      else item.fittings.push({ type: fit.type, count: 1 });
    }
    return Array.from(map.values()).sort((a, b) => a.size.localeCompare(b.size));
  }, [segments, fittings, activeMaterial]);

  const totalFootage = useMemo(() => segments.reduce((sum, s) => sum + s.lengthFt, 0), [segments]);

  return {
    segments, fittings, mode, setMode,
    activeSize, setActiveSize, activeMaterial, setActiveMaterial,
    activeFitting, setActiveFitting,
    drawStart, setDrawStart,
    addSegment, addFitting, deleteAtPoint, clearAll,
    moveSegment, moveFitting,
    undo, redo, canUndo: undoStack.length > 0, canRedo: redoStack.length > 0,
    cutList, totalFootage,
    sketchList, activeSketchId,
    saveAsNewSketch, loadSketch, deleteSketch, renameSketch, newBlankSketch,
  };
}
